import { useCallback, useEffect, useState } from "react";
import { css } from "styled-system/css";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { TriangleAlert } from "lucide-react";
import { GOOGLE_EVENT_COLORS } from "@/lib/google-calendar-colors";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useConfirm } from "@/hooks/useConfirm";

type StaffRow = {
  id: string;
  name: string;
  googleColorId: string;
  googleColorNeedsReview: boolean;
  isActive: boolean;
  sortOrder: number;
  googleCalendarId: string | null;
  googleCalendarName: string | null;
  googleCalendarBgColor: string | null;
};

type GoogleCalInfo = { id: string; summary: string; backgroundColor: string | null; primary: boolean };

export function DashboardAgendaStaff() {
  const { refresh } = useAuth();
  const confirm = useConfirm();
  const [staff, setStaff] = useState<StaffRow[] | null>(null);
  const [newName, setNewName] = useState("");
  const [newColorId, setNewColorId] = useState("6");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleCalendars, setGoogleCalendars] = useState<GoogleCalInfo[]>([]);

  const load = useCallback(async () => {
    const [rStaff, rGoogle] = await Promise.all([
      fetchWithSessionSync("/api/dashboard/agenda-staff", { credentials: "include" }, refresh),
      fetchWithSessionSync("/api/integrations/google/me", { credentials: "include" }, refresh),
    ]);

    if (!rStaff.ok) {
      const j = (await rStaff.json().catch(() => ({}))) as { error?: string; code?: string };
      setStaff([]);
      setMsg(
        j.error ??
          (rStaff.status === 503
            ? "El servicio no está disponible en este momento. Probá de nuevo en unos minutos."
            : "No se pudo cargar la lista."),
      );
    } else {
      setMsg(null);
      const d = (await rStaff.json()) as { staff: StaffRow[] };
      setStaff(d.staff);
    }

    if (rGoogle.ok) {
      const g = (await rGoogle.json()) as { connected: boolean };
      setGoogleConnected(g.connected);
      if (g.connected) {
        const rCal = await fetchWithSessionSync("/api/integrations/google/calendars", { credentials: "include" }, refresh);
        if (rCal.ok) {
          const d = (await rCal.json()) as { calendars: GoogleCalInfo[] };
          setGoogleCalendars(d.calendars);
        }
      }
    }
  }, [refresh]);

  useEffect(() => {
    void load();
  }, [load]);

  async function addStaff() {
    if (!newName.trim()) return;
    setBusy(true);
    setMsg(null);
    const r = await fetchWithSessionSync(
      "/api/dashboard/agenda-staff",
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newName.trim(), googleColorId: newColorId }),
      },
      refresh,
    );
    setBusy(false);
    if (!r.ok) {
      setMsg("No se pudo agregar.");
      return;
    }
    setNewName("");
    await load();
  }

  async function updateStaff(id: string, patch: Partial<Pick<StaffRow, "name" | "googleColorId" | "isActive">>) {
    setBusy(true);
    await fetchWithSessionSync(
      `/api/dashboard/agenda-staff/${id}`,
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      },
      refresh,
    );
    setBusy(false);
    await load();
  }

  async function linkCalendar(staffId: string, calId: string | null) {
    const cal = googleCalendars.find((c) => c.id === calId);
    await fetchWithSessionSync(
      `/api/dashboard/agenda-staff/${staffId}/google-calendar`,
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          googleCalendarId: calId,
          googleCalendarName: cal?.summary ?? null,
          googleCalendarBgColor: cal?.backgroundColor ?? null,
        }),
      },
      refresh,
    );
    await load();
  }

  async function removeStaff(id: string, name: string) {
    if (
      !(await confirm({
        message: `¿Eliminar a ${name}? Los turnos existentes quedarán sin este profesional asignado.`,
        tone: "danger",
        confirmLabel: "Eliminar",
      }))
    )
      return;
    setBusy(true);
    await fetchWithSessionSync(`/api/dashboard/agenda-staff/${id}`, { method: "DELETE", credentials: "include" }, refresh);
    setBusy(false);
    await load();
  }

  if (staff === null) {
    return (
      <DashboardPageShell eyebrow="Configuración" title="Profesionales de la agenda">
        <LoadingFrame variant="light" minHeight={240} />
      </DashboardPageShell>
    );
  }

  return (
    <DashboardPageShell
      eyebrow="Configuración"
      title="Profesionales de la agenda"
      description="Definí quién atiende en esta agenda y el color del turno en Google Calendar (mismos tonos que la app de Google). Al crear o reservar un turno, asigná el profesional para que el evento se sincronice con el color correcto."
    >
      {msg && staff.length === 0 ? (
        <p className={css({ fontSize: "sm", color: "#b45309", mb: "4", p: "4", rounded: "lg", bg: "#fffbeb", borderWidth: "1px", borderColor: "#fcd34d" })}>
          {msg}
        </p>
      ) : null}
      <div className={css({ rounded: "xl", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.card", p: "5", maxW: "40rem" })}>
        <h2 className={css({ fontWeight: "semibold", mb: "3" })}>Agregar profesional</h2>
        <div className={css({ display: "flex", flexWrap: "wrap", gap: "3", alignItems: "flex-end" })}>
          <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm", flex: "1", minW: "160px" })}>
            Nombre (ej. Maxi, Fer)
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Maxi" />
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm" })}>
            Color en Google
            <select
              className={css({ px: "3", py: "2", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", fontSize: "sm" })}
              value={newColorId}
              onChange={(e) => setNewColorId(e.target.value)}
            >
              {GOOGLE_EVENT_COLORS.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <div className={css({ display: "flex", gap: "2", alignItems: "center", pb: "1" })}>
            <span
              className={css({ w: "6", h: "6", rounded: "full", borderWidth: "1px", borderColor: "rgba(0,0,0,0.1)" })}
              style={{ background: GOOGLE_EVENT_COLORS.find((c) => c.id === newColorId)?.hex }}
            />
            <Button type="button" disabled={busy || !newName.trim()} onClick={() => void addStaff()}>
              Agregar
            </Button>
          </div>
        </div>
        {msg ? <p className={css({ fontSize: "sm", color: "red.700", mt: "2" })}>{msg}</p> : null}
      </div>

      <div className={css({ rounded: "xl", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.card", p: "5", mt: "5" })}>
        <h2 className={css({ fontWeight: "semibold", mb: "3" })}>Listado ({staff.length})</h2>
        {staff.length === 0 ? (
          <p className={css({ fontSize: "sm", color: "fg.muted" })}>
            Todavía no cargaste profesionales. Sin esto, los turnos en Google usan solo el nombre de la agenda en el título.
          </p>
        ) : (
          <ul className={css({ display: "flex", flexDir: "column", gap: "4" })}>
            {staff.map((s) => (
              <li
                key={s.id}
                className={css({
                  display: "flex",
                  flexDir: "column",
                  gap: "2",
                  p: "3",
                  rounded: "lg",
                  bg: s.isActive ? "bg.muted" : "transparent",
                  opacity: s.isActive ? 1 : 0.6,
                })}
              >
                <div className={css({ display: "flex", flexWrap: "wrap", gap: "3", alignItems: "center" })}>
                  <span
                    className={css({ w: "4", h: "4", rounded: "full", flexShrink: 0 })}
                    style={{ background: GOOGLE_EVENT_COLORS.find((c) => c.id === s.googleColorId)?.hex ?? "#5484ed" }}
                  />
                  <Input
                    defaultValue={s.name}
                    className={css({ maxW: "180px" })}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== s.name) void updateStaff(s.id, { name: v });
                    }}
                  />
                  <select
                    className={css({ px: "2", py: "1.5", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", fontSize: "sm" })}
                    value={s.googleColorId}
                    onChange={(e) => void updateStaff(s.id, { googleColorId: e.target.value })}
                    disabled={busy}
                  >
                    {GOOGLE_EVENT_COLORS.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  {s.googleColorNeedsReview && (
                    <span
                      title="Este color se sugirió automáticamente pero no hay un match claro con el color del calendario en Google. Confirmalo o elegí otro."
                      className={css({
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "1",
                        fontSize: "xs",
                        color: "#92400e",
                        bg: "#fffbeb",
                        borderWidth: "1px",
                        borderColor: "#fcd34d",
                        rounded: "md",
                        px: "2",
                        py: "1",
                      })}
                    >
                      <TriangleAlert size={14} strokeWidth={2} />
                      Confirmar color
                    </span>
                  )}
                  <label className={css({ fontSize: "xs", display: "flex", alignItems: "center", gap: "1" })}>
                    <input
                      type="checkbox"
                      checked={s.isActive}
                      onChange={(e) => void updateStaff(s.id, { isActive: e.target.checked })}
                    />
                    Activo
                  </label>
                  <Button type="button" variant="secondary" disabled={busy} onClick={() => void removeStaff(s.id, s.name)}>
                    Eliminar
                  </Button>
                </div>

                {/* Vínculo con Google Calendar */}
                <div className={css({ display: "flex", alignItems: "center", gap: "2", pl: "7" })}>
                  {!googleConnected ? (
                    <span className={css({ fontSize: "xs", color: "#94a3b8" })}>
                      <a href="/dashboard/settings/google-calendar" className={css({ color: "#4f46e5", textDecoration: "underline" })}>
                        Conectá Google Calendar
                      </a>{" "}
                      para vincular agendas
                    </span>
                  ) : (
                    <>
                      {s.googleCalendarBgColor && (
                        <span
                          className={css({ w: "3", h: "3", rounded: "full", flexShrink: 0, borderWidth: "1px", borderColor: "rgba(0,0,0,0.1)" })}
                          style={{ backgroundColor: s.googleCalendarBgColor }}
                        />
                      )}
                      <select
                        className={css({ px: "2", py: "1", rounded: "md", borderWidth: "1px", borderColor: "#e2e8f0", fontSize: "xs", color: "#334155", bg: "white" })}
                        value={s.googleCalendarId ?? ""}
                        onChange={(e) => void linkCalendar(s.id, e.target.value || null)}
                        disabled={busy}
                      >
                        <option value="">Sin agenda de Google</option>
                        {googleCalendars.map((gc) => (
                          <option key={gc.id} value={gc.id}>
                            {gc.summary}
                            {gc.primary ? " (principal)" : ""}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className={css({ fontSize: "xs", color: "fg.muted", mt: "4", maxW: "48rem", lineHeight: "1.6" })}>
        Tip: si un turno en Google se ve una hora desplazada, revisá que la zona horaria de la cuenta en{" "}
        <strong>Configuración → General</strong> coincida con la de tu Google Calendar (ej. Argentina).
      </p>
    </DashboardPageShell>
  );
}
